import { Injectable } from '@angular/core';
import { LogService } from '../../../shared/services/log.service';
import { ToastService } from '../../../shared/services/toastr.service';
import { TranslateService } from '@ngx-translate/core';
import { AccountService } from '../../../shared/services/account.service';
import { PlatformState } from './platform_state';
import { Store } from '@ngrx/store';
import { updatePopover, updateSaving, updateWebsiteInvalid } from './platform_actions_creator';
import { RedirectService } from '../../../shared/services/redirect.service';
import { Angulartics2Facebook } from 'angulartics2/facebook';
import { PlatformService } from '../../../shared/services/platforms/platform.service';
import { FlowTrackingService } from '../../shared/services/flow-tracking.service';
import { getPlatformByKey } from '../../../shared/services/constants/platform_types';
import { DEFAULT_INTENT } from './product_intent';
import { take } from 'rxjs/operators';
import { AnalyticsTrackingProvider } from '@yotpo-ui/core';

@Injectable()
export class PlatformEffectsService {

  private knownErrors = {
    'website': this.handleWebSiteError
  };
  private error = '';

  constructor(private logger: LogService,
              private toastrService: ToastService,
              private translate: TranslateService,
              private redirectService: RedirectService,
              private store: Store<PlatformState>,
              private accountService: AccountService,
              private redCrossService: AnalyticsTrackingProvider,
              private fbAnalytics: Angulartics2Facebook,
              private platformService: PlatformService,
              private flowTrackingService: FlowTrackingService
  ) { }

  platformFlow(platformState: PlatformState, productIntent: string): void {
    productIntent = productIntent || DEFAULT_INTENT;
    if (this.platformService.shouldAuthenticate(platformState.platform)) {
      this.platformService.navigate(platformState.platform);
    } else {
      if (!this.isAValidUrl(platformState.website)) {
        this.handleWebSiteError(this.store);
        return;
      }
      let enableProductParams = this.accountService.buildEnableProductParams(platformState.platform, platformState.website, productIntent, platformState.phone, platformState.maxMonthlyOrders);
      this.accountService.enable_product(enableProductParams).pipe(take(1)).subscribe((enableProductResponse: any) => {
        if (enableProductResponse.error_website) {
          this.handleWebSiteError(this.store);
          return;
        }
        if (enableProductResponse.status.redirect_url &&
          (enableProductResponse.response.store_info.app_key && enableProductResponse.response.admin_info.token)) {
          this.handleSuccess(enableProductResponse, platformState.platform, productIntent);
        } else {
          this.handleError(enableProductResponse);
        }
      }, (errorResponse) => {
        this.handleError(errorResponse);
      });
    }
  }

  private async handleSuccess(enableProductResponse, platform, productIntent) {
    let redirectUrl = enableProductResponse.status.redirect_url;
    let platformName = getPlatformByKey('id', platform) || getPlatformByKey('name', platform) || '';

    await this.flowTrackingService.StepTwoCompleted();

    // we skip on sending events for reviews product in Shopify since it will be sent later in when the user return from
    // Shopify to complete the registration - finishRegistrationService
    if (productIntent.toUpperCase() !== 'REVIEWS' || platformName.toUpperCase() !== 'SHOPIFY') {
      this.fbAnalytics.eventTrack('CompleteRegistration');
      await this.redCrossService.trackAsync('Signup Completed');
    }

    this.redirectService.navigateOrRedirectTo(new URL(redirectUrl));
  }

  private handleError(response) {
    if (this.knownError(response)) {
      this.handleKnownError();
    } else {
      this.handleUnknownError(response);
    }
  }

  private knownError(response) {
    let knownError = false;
    if (this.webSiteError(response)) {
      this.error = 'website';
      knownError = true;
    }
    return knownError;
  }

  private webSiteError(response) {
    return response.message.toLowerCase().lastIndexOf('url is invalid') > -1;
  }

  private handleKnownError() {
    this.knownErrors[this.error](this.store);
  }

  private handleWebSiteError(store) {
    store.dispatch(updateWebsiteInvalid({website_invalid: true}));
    store.dispatch(updateSaving({saving: false}));
    store.dispatch(updatePopover({popover: true}));
  }

  private handleUnknownError(response) {
    this.logger.error(response);
    let errMsg = this.translate.instant('SIGNUP.PLATFORM.SAVE_FAILED');
    this.toastrService.error('signup', 'platformFlow', errMsg, response);
  }

  private isAValidUrl(value: string): boolean {
    const pattern = new RegExp('^(https?:\\/\\/)?([\\da-zA-Z\\.-]+)\\.([\\da-zA-Z\\.]{2,6})([\\/\\w\\.\\-~]*)*\\/?$');
    return !!pattern.test(value);
  }
}
